import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  FamilyRestroom as FamilyIcon,
  Event as EventIcon,
  PhotoLibrary as PhotoIcon,
  Description as DocumentIcon,
  HealthAndSafety as HealthIcon,
  AccountTree as TreeIcon,
  Forum as DiscussionIcon,
  EmojiEvents as GoalsIcon,
  Restaurant as RecipeIcon,
  ContactPhone as ContactIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircleOutline as CheckIcon
} from '@mui/icons-material';

const features = [
  { icon: <FamilyIcon />, title: 'Family Board', description: 'A central dashboard for all your family activity — announcements, upcoming events, recent photos, and health updates at a glance.' },
  { icon: <EventIcon />, title: 'Events & Announcements', description: 'Plan and share family events with RSVP tracking. Post announcements to keep everyone informed.' },
  { icon: <DiscussionIcon />, title: 'Messages & Discussions', description: 'Direct messages and group discussion threads to keep family conversations organized.' },
  { icon: <PhotoIcon />, title: 'Photos', description: 'Upload and share family photos in organized albums. Keep memories safe and accessible to the whole family.' },
  { icon: <DocumentIcon />, title: 'Documents', description: 'Securely store and share important family documents, with fine-grained permissions for who can view or edit.' },
  { icon: <HealthIcon />, title: 'Health Updates', description: 'Track and share health information for family members — appointments, conditions, and updates.' },
  { icon: <TreeIcon />, title: 'Family Tree', description: 'Visualize your family history with an interactive tree that connects generations.' },
  { icon: <GoalsIcon />, title: 'Goals', description: 'Set and track family goals together — from savings targets to fitness milestones.' },
  { icon: <RecipeIcon />, title: 'Recipes', description: 'Save and share your family\'s favorite recipes in a private, organized recipe collection.' },
  { icon: <ContactIcon />, title: 'Family Directory', description: 'A private address book for all family members with contact details, birthdays, and profiles.' },
  { icon: <SecurityIcon />, title: 'Secure Vault', description: 'A locked vault for sensitive documents and information with granular access controls.' },
  { icon: <AdminIcon />, title: 'Family Administration', description: 'Manage family members, roles, and permissions through a dedicated admin portal.' },
];

const values = [
  'Privacy first — your family data belongs to your family',
  'Secure by design — role-based permissions on everything',
  'No ads — ever',
  'Works across all devices',
  'Designed for multigenerational families',
];

export default function AboutPage() {
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          p: 5,
          mb: 4,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: 'rgba(255,255,255,0.2)',
            mx: 'auto',
            mb: 2,
            fontSize: 40
          }}
        >
          <FamilyIcon sx={{ fontSize: 48 }} />
        </Avatar>
        <Typography variant="h3" fontWeight={700} gutterBottom>
          FamilyForLife
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 560, mx: 'auto' }}>
          A private, secure platform that brings families closer together — wherever life takes you.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Chip label="Version 1.0 — MVP" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
        </Box>
      </Paper>

      {/* Mission */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Our Mission
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          FamilyForLife was built on a simple belief: families deserve a dedicated, private space to stay connected,
          organized, and informed — without giving up their data to social media platforms or advertising networks.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Every feature is designed around real family needs: sharing memories, coordinating events, tracking health,
          securing important documents, and celebrating the milestones that matter most.
        </Typography>
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          What we stand for
        </Typography>
        <List dense>
          {values.map((value) => (
            <ListItem key={value} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={value} />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Features */}
      <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
        Everything your family needs, in one place
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {features.map((feature) => (
          <Grid item xs={12} sm={6} key={feature.title}>
            <Paper
              elevation={1}
              sx={{
                p: 2.5,
                height: '100%',
                borderRadius: 2,
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                '&:hover': { elevation: 3, boxShadow: 3 },
                transition: 'box-shadow 0.2s'
              }}
            >
              <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', flexShrink: 0 }}>
                {feature.icon}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Privacy & Security */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 2, bgcolor: 'action.hover' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <SecurityIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Privacy & Security
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          FamilyForLife uses Firebase Authentication and Firestore with strict server-side security rules.
          Every piece of data is scoped to your family — no other family can access your information.
          Role-based permissions let administrators control exactly who can see, edit, or delete content.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We do not sell or share your data with third parties. Your family's information is stored securely
          and is only accessible to authenticated members of your family group.
        </Typography>
      </Paper>

      {/* Footer note */}
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="caption" color="text.secondary">
          FamilyForLife &copy; {new Date().getFullYear()} &nbsp;&bull;&nbsp; Built with love for families everywhere
        </Typography>
      </Box>
    </Box>
  );
}
